import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Settings → Data: phone normalization DRY-RUN report, then MERGE only after the owner confirms.
export default function PhoneCleanupPanel() {
  const [rep, setRep] = useState(null);
  const [ic, setIc] = useState(null);
  const [busy, setBusy] = useState(false);
  const runIc = async () => {
    setBusy(true);
    try { const { data } = await api.get("/admin/items-cleanup/dry-run", { timeout: 120000 }); setIc(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const applyIc = async () => {
    if (window.prompt(`Items clean-up APPLY karne ke liye APPLY likhiye (${ic.changes.length} changes, koi item delete nahi hoga).`) !== "APPLY") return;
    setBusy(true);
    try { const { data } = await api.post("/admin/items-cleanup/apply?confirm=APPLY", null, { timeout: 300000 }); toast.success(`Applied ✓ ${data.changed} items updated`); runIc(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const icPanel = (
    <div className="surface p-4 rounded-sm space-y-3 mb-4" data-testid="items-cleanup-panel">
      <div className="text-[10px] font-mono-num tracking-widest text-orange-300">// ITEMS CLEAN-UP (links, Center Lock, categories, sort)</div>
      <div className="flex gap-2">
        <button onClick={runIc} disabled={busy} className="h-9 px-4 rounded-sm border border-orange-500 text-orange-200 text-xs font-heading tracking-widest" data-testid="items-cleanup-dry-run-btn">DRY-RUN REPORT</button>
        {ic && ic.changes.length > 0 && <button onClick={applyIc} disabled={busy} className="h-9 px-4 rounded-sm bg-red-600 text-white text-xs font-heading tracking-widest" data-testid="items-cleanup-apply-btn">APPLY ({ic.changes.length})</button>}
      </div>
      {ic && (
        <div className="text-xs space-y-2" data-testid="items-cleanup-report">
          <div className="grid grid-cols-2 gap-2 font-mono-num">
            {["before", "after"].map((k) => (
              <div key={k} className="surface-2 p-2 rounded-sm"><b>{k.toUpperCase()}</b>: total {ic[k].total_items} · active {ic[k].active} · linked {ic[k].linked} · dropdown sheets {ic[k].dropdown_sheets} / hardware {ic[k].dropdown_hardware} / service {ic[k].dropdown_service} · to-confirm {ic[k].to_confirm} · Center Lock in dropdown {ic[k].center_lock_in_dropdown}</div>
            ))}
          </div>
          {ic.links.map((l) => (
            <div key={l.master_id} className="text-slate-300">{l.master} (stock {l.master_stock}) ← {l.linked.map((k) => `${k.name}${k.already ? " ✓" : " (new link)"}`).join(", ")} · old linked balance {l.old_linked_balance} (not counted)</div>
          ))}
          {ic.center_lock.items.length > 0 && (
            <div className="text-slate-300">Center Lock: {ic.center_lock.items.map((c) => `${c.name} [stock ${c.stock}, cost ₹${c.cost}, sale ₹${c.sale}, ${c.orders} orders]`).join(" | ")} → MAIN: {ic.center_lock.keep?.name}</div>
          )}
          {ic.notes.map((n) => <div key={n} className="text-amber-300">{n}</div>)}
          <div className="max-h-56 overflow-y-auto surface-2 p-2 rounded-sm">
            {ic.changes.map((c) => <div key={c.id}>{c.name}: {c.why.join("; ")}</div>)}
          </div>
        </div>
      )}
    </div>
  );
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.get("/admin/phone-migration/dry-run", { timeout: 120000 }); setRep(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const merge = async () => {
    if (window.prompt("Duplicate customers MERGE karne ke liye MERGE likhiye. Backup pehle liya jayega.") !== "MERGE") return;
    setBusy(true);
    try { const { data } = await api.post("/admin/phone-migration/apply?confirm=MERGE", null, { timeout: 300000 }); toast.success(`Merged ✓ orders tagged ${data.orders_tagged}, advance docs merged ${data.credit_docs_merged}`); run(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <>
    {icPanel}
    <div className="surface p-4 rounded-sm space-y-3" data-testid="phone-cleanup-panel">
      <div className="text-[10px] font-mono-num tracking-widest text-cyan-300">// CUSTOMER PHONE CLEANUP (same number, alag format)</div>
      <div className="flex gap-2">
        <button onClick={run} disabled={busy} className="h-9 px-4 rounded-sm border border-cyan-500 text-cyan-200 text-xs font-heading tracking-widest" data-testid="phone-dry-run-btn">{busy ? "…" : "DRY-RUN REPORT"}</button>
        {rep && rep.groups_needing_fix > 0 && (
          <button onClick={merge} disabled={busy} className="h-9 px-4 rounded-sm bg-red-600 text-white text-xs font-heading tracking-widest" data-testid="phone-merge-btn">MERGE ({rep.groups_needing_fix} groups)</button>
        )}
      </div>
      {rep && (
        <div className="space-y-2 text-xs" data-testid="phone-dry-run-report">
          <div className="text-slate-300">Customers: {rep.customers_total} · Groups to fix: <b>{rep.groups_needing_fix}</b> · Galat number: <b>{rep.invalid_phones.length}</b></div>
          {rep.groups.map((g) => (
            <div key={g.phone_normalized} className="surface-2 p-2 rounded-sm" data-testid={`phone-group-${g.phone_normalized}`}>
              <div className="font-mono-num text-white">{g.phone_normalized} · {g.names.join(", ")}</div>
              <div className="text-slate-400">Formats: {g.raw_mobiles.join(" | ")} · {g.order_count} orders · {g.payment_count} payments · {g.advance_docs.length} advance records</div>
              <div className="text-slate-300">Ledger balance before {inr(g.ledger_before?.balance_due)} → after {inr(g.after.balance_due)} · Payment-In cards before: {g.payment_in_cards_before.map((c) => `${c.mobile}=${inr(c.due_shown)}`).join(", ")} → after 1 card {inr(g.after.balance_due)}</div>
            </div>
          ))}
          {rep.invalid_phones.length > 0 && (
            <div className="text-amber-300" data-testid="phone-invalid-list">Galat numbers (aap khud theek karein): {rep.invalid_phones.map((x) => `${x.order_number || x.customer_name} (${x.mobile})`).join(", ")}</div>
          )}
        </div>
      )}
    </div>
    </>
  );
}
